'use client';

/**
 * Arcade-home navigation (design_handoff_arcade_tab_bar_v5): Play / Store / Menu.
 * Mobile → floating pill (bottom); desktop → left rail (Menu pinned to the bottom).
 * Data-driven `ITEMS` so a 4th "Community" tab is additive, not a rewrite.
 *
 * Themed via the partner CSS-var contract (no per-partner hex): active = the
 * NAV colour (`--partner-tab-active` — green on Skillz, blue on Lycos: the v6
 * magenta-accent / green-nav split), idle = `--partner-text-muted`, surfaces from
 * the wallet/divider/card tokens so one component serves a light tenant (Lycos)
 * and a dark one (Skillz/Coverd). Arcade-home only — not the tutorial or game page.
 *
 * Play (active) + Store are links; Menu (the player avatar) opens the de-gamed Skillz
 * Side Menu v2 by dispatching a window event the PartnerSdkRuntime island listens for
 * (the tab bar is server-rendered and has no access to that client island's state).
 */

import Link from 'next/link';
import {usePathname} from 'next/navigation';

const TOGGLE_MENU_EVENT = 'partner:toggle-menu';

type TabId = 'play' | 'store' | 'menu';
export type TabBarPlacement = 'floating' | 'rail';

interface PartnerTabBarProps {
  slug: string;
  /** Signed-in player's avatar for the Menu item; neutral ring fallback if absent. */
  avatarUrl?: string;
  placement: TabBarPlacement;
  className?: string;
  /** Root ("/") multicade surface (vanilla Skillz, no tenant): Play/Store link to
   *  the root routes (`/`, `/store`) instead of `/partner/{slug}/…`. */
  rootMode?: boolean;
  /**
   * Deferred auth (§9): base account-creation path for a signed-out root GUEST.
   * When set, Play stays free-browse (`/`), but Store and Menu are gated — they
   * link to `${guestAuthHref}?returnTo=…` (the existing signup) instead of the
   * store route / menu drawer, so the guest signs up and resumes the intent.
   */
  guestAuthHref?: string;
}

function PlayIcon() {
  // Filled "play button" circle with the triangle PUNCHED OUT (evenodd), not painted a
  // fixed white. On the active desktop-rail pill the icon color is --partner-nav-on (white
  // on Lycos), so a white triangle on that white circle was invisible; a cut-out shows the
  // pill/background behind it instead, so the arrow always contrasts with the circle in
  // every state (idle muted, active accent, on-fill white) and on any background.
  return (
    <svg aria-hidden viewBox='0 0 28 28' width={27} height={27}>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        fill='currentColor'
        d='M14 0a14 14 0 1 1 0 28 14 14 0 0 1 0-28ZM11 8.5l9 5.5-9 5.5z'
      />
    </svg>
  );
}

function StoreIcon() {
  // Line-art shopping bag, 2px stroke, round caps/joins (handoff).
  return (
    <svg
      aria-hidden
      viewBox='0 0 24 24'
      width={22}
      height={22}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z' />
      <path d='M3 6h18M16 10a4 4 0 0 1-8 0' />
    </svg>
  );
}

export default function PartnerTabBar({
  slug,
  avatarUrl,
  placement,
  className,
  rootMode = false,
  guestAuthHref,
}: PartnerTabBarProps) {
  const enc = encodeURIComponent(slug);
  const playHref = rootMode ? '/' : `/partner/${enc}`;
  const storeHref = rootMode ? '/store' : `/partner/${enc}/store`;
  // Active tab is derived from the route (not a prop) so the same shell serves both
  // the Play and Store tabs — the tab bar renders once and the highlight follows
  // navigation. Store when the path ends in `/store` (with or without Next's
  // trailingSlash), Play otherwise (Menu is a drawer, never a "current page").
  const pathname = usePathname();
  const activeTab: TabId = /\/store\/?$/.test(pathname ?? '') ? 'store' : 'play';
  // Guest gate: Store + Menu require an account, so a signed-out root visitor is
  // sent to signup with the intent's `returnTo`; Play stays free-browse.
  const gate = (returnTo: string) =>
    `${guestAuthHref}${guestAuthHref?.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(returnTo)}`;
  const items: Array<{
    id: TabId;
    label: string;
    href: string | null;
    kind: TabId;
    onPress?: () => void;
  }> = [
    {id: 'play', label: 'Play', href: playHref, kind: 'play'},
    {
      id: 'store',
      label: 'Store',
      href: guestAuthHref ? gate(storeHref) : storeHref,
      kind: 'store',
    },
    // Menu opens the de-gamed Side Menu v2 (PartnerSdkRuntime). For a guest there is
    // no session/drawer yet, so it routes to create-profile and resumes to the arcade
    // WITH the menu opened (`?menu=open` → PartnerSdkRuntime pops the drawer on return),
    // so a Menu tap lands back home with the menu showing — what they reached for.
    {
      id: 'menu',
      label: 'Menu',
      href: guestAuthHref ? gate(`${playHref}?menu=open`) : null,
      kind: 'menu',
      onPress: guestAuthHref
        ? undefined
        : () => window.dispatchEvent(new CustomEvent(TOGGLE_MENU_EVENT)),
    },
  ];

  // Idle text = the muted token; active = the nav colour. On a filled rail pill the
  // ink is the derived on-nav colour (white on Lycos blue, green on the Skillz dark
  // tint) — see --partner-nav-fill/on in themeVars.
  const idleColor = 'var(--partner-text-muted)';
  const rail = placement === 'rail';

  const renderInner = (
    kind: 'play' | 'store' | 'menu',
    label: string,
    active: boolean,
    // Active desktop-rail item sits on the --partner-nav-fill pill, so its
    // icon/label/avatar-ring take --partner-nav-on rather than the raw nav colour.
    onFill: boolean
  ) => {
    const color = onFill
      ? 'var(--partner-nav-on)'
      : active
        ? 'var(--partner-tab-active)'
        : idleColor;
    const ring = color;
    const avatarSize = rail ? 30 : 24;
    return (
      <>
        {kind === 'menu' ? (
          avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- player avatar is a remote asset
            <img
              src={avatarUrl}
              alt=''
              width={avatarSize}
              height={avatarSize}
              className='rounded-full object-cover'
              style={{border: `2px solid ${ring}`}}
            />
          ) : (
            // Signed-out: no avatar yet, so show a neutral person silhouette placeholder
            // inside the ring (an empty circle read as "broken").
            <span
              className='flex items-center justify-center rounded-full'
              style={{
                width: avatarSize,
                height: avatarSize,
                border: `2px solid ${idleColor}`,
                backgroundColor: 'color-mix(in srgb, var(--partner-text) 8%, transparent)',
                color: idleColor,
              }}
            >
              <svg
                aria-hidden
                viewBox='0 0 24 24'
                width={Math.round(avatarSize * 0.64)}
                height={Math.round(avatarSize * 0.64)}
                fill='currentColor'
              >
                <path d='M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.6 0-8 1.8-8 5v1h16v-1c0-3.2-4.4-5-8-5Z' />
              </svg>
            </span>
          )
        ) : (
          <span style={{color}}>{kind === 'play' ? <PlayIcon /> : <StoreIcon />}</span>
        )}
        <span
          style={{color, fontWeight: active ? 800 : 700, fontSize: rail ? 11 : 10.5}}
          // leading-tight (not leading-none): a 1.0 line box clips the taller Bold/ExtraBold
          // Open Sans glyphs and overlaps wrapped lines; whitespace-nowrap keeps short labels
          // on one line. Both symptoms read as "truncated" — this covers both.
          className='leading-tight whitespace-nowrap'
        >
          {label}
        </span>
      </>
    );
  };

  const itemBase =
    'flex flex-col items-center gap-1 rounded-xl transition-[transform,background-color] duration-150 active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
  const railItem = 'w-[72px] py-3 hover:bg-[color-mix(in_srgb,var(--partner-text)_6%,transparent)] rounded-xl';

  const content = items.map((it) => {
    const active = it.id === activeTab;
    const activeRailBg = active && rail;
    const inner = renderInner(it.kind, it.label, active, activeRailBg);
    const style = activeRailBg ? {backgroundColor: 'var(--partner-nav-fill)'} : undefined;
    const cls = `${itemBase} ${rail ? railItem : 'flex-1'} ${it.id === 'menu' && rail ? 'mt-auto' : ''}`;

    if (it.href) {
      return (
        <Link key={it.id} href={it.href} className={cls} style={style} aria-current={active ? 'page' : undefined}>
          {inner}
        </Link>
      );
    }
    return (
      <button key={it.id} type='button' onClick={it.onPress} className={cls} style={style}>
        {inner}
      </button>
    );
  });

  if (rail) {
    return (
      <nav
        aria-label='Arcade'
        // Stable hook for anything that needs to measure the rail.
        data-arcade-rail
        // `relative z-10` is load-bearing on the STORE tab: MulticadeStoreShell paints a
        // `fixed inset-0 -z-10` backdrop inside <main>'s `isolate` context, and <main>
        // comes after this rail in DOM with no z-index — so the whole store column (backdrop
        // included) painted ON TOP of the rail, hiding it the moment the store body mounted
        // (the rail was visible only during the loading state, before that column existed).
        // It's pointer-events-none, so hit-testing still found the rail while paint showed
        // navy over it. z-10 lifts the rail above the store column; the z-30 sticky header
        // still sits above the rail.
        // `lg:sticky lg:self-start` + the fixed height make this a pinned, viewport-tall
        // sidebar rather than a column stretched to the full catalogue height. Without it the
        // Menu tab (mt-auto → bottom of the rail) sat at the bottom of a very tall rail and you
        // had to scroll the whole page to reach it; now it rides the bottom of the visible rail.
        className={`relative z-10 hidden shrink-0 flex-col items-center gap-2 self-stretch border-r px-0 py-[18px] lg:flex lg:sticky lg:self-start ${className ?? ''}`}
        style={{
          // The rail's width IS `--arcade-rail-width` — the same custom property
          // ArcadeShell publishes on :root for overlays that portal to document.body
          // (they sit outside the shell and must offset by the rail to line up with
          // the content column). Reading it here instead of repeating `w-[92px]`
          // means the published value and the real rail width cannot drift apart.
          width: 'var(--arcade-rail-width)',
          // Pin below the sticky header (--arcade-rail-top) and stand exactly one viewport
          // tall, so the bottom-anchored Menu tab is always in view.
          top: 'var(--arcade-rail-top, 0px)',
          height: 'calc(100dvh - var(--arcade-rail-top, 0px))',
          backgroundColor: 'var(--partner-wallet-bg)',
          borderColor: 'var(--partner-divider)',
        }}
      >
        {content}
      </nav>
    );
  }

  // Mobile floating pill.
  return (
    <nav
      aria-label='Arcade'
      className={`fixed inset-x-5 bottom-[18px] z-20 flex h-[62px] items-center justify-around rounded-full border px-[22px] lg:hidden ${className ?? ''}`}
      style={{
        backgroundColor: 'color-mix(in srgb, var(--partner-card) 94%, transparent)',
        borderColor: 'var(--partner-card-border)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        boxShadow: '0 12px 34px rgba(0,0,0,0.42)',
      }}
    >
      {content}
    </nav>
  );
}
